import { rufe } from './client';

/**
 * Termine: anlegen, verlegen, absagen (BUC-01, BUC-08, BUC-138, ÄA-04).
 */

export type Terminzeile = {
  readonly id: string;
  readonly artikel: string;
  readonly terminart: 'offen' | 'exklusiv';
  readonly beginn: string;
  readonly ende: string;
  readonly hoechstteilnehmer: number;
  readonly mindestteilnehmer: number;
  readonly status: string;
};

export type Terminauswahl = Terminzeile & { readonly frei: number };

/**
 * Ein Termin, wie die VERWALTUNG ihn sieht — mit Art, Zustand und Herkunft.
 *
 * `buchbare` ist die Buchungsauswahl und zeigt nur, worauf sich jetzt buchen
 * lässt: offene Termine im Zustand „buchbar". Wer plant, will sehen, was da
 * ist — auch das, was gerade erst angelegt wurde („geplant") und alles
 * Exklusive, das nach BUC-67 aus der Buchungsauswahl herausfällt.
 */
export type Terminuebersicht = Terminzeile & {
  readonly ort: string | null;
  readonly herkunftangebot: string | null;
  readonly herkunftauftrag: string | null;
  /** Vergebene Plätze in der anonymen Sicht (KAP-22). */
  readonly belegt: number;
};

/**
 * Ein Termin für die Kalenderansicht (TAG-29).
 *
 * `tag` ist der Kalendertag in Europe/Berlin — vom Server vorgerechnet und
 * nicht dem Browser überlassen: FRS-01 macht die Zone maßgeblich, und die
 * Gruppierung nach Tagen darf nicht davon abhängen, wo jemand sitzt.
 */
export type Kalendertermin = {
  readonly id: string;
  readonly artikel: string;
  readonly artikelnummer: string;
  readonly artikelbezeichnung: string;
  readonly terminart: 'offen' | 'exklusiv';
  readonly beginn: string;
  readonly ende: string;
  readonly ort: string | null;
  readonly status: string;
  readonly hoechstteilnehmer: number;
  readonly mindestteilnehmer: number;
  readonly tag: string;
  /** Vergebene Plätze in der anonymen Sicht (KAP-22). */
  readonly belegt: number;
  /**
   * Aus welcher Terminoptionsanfrage dieser Termin stammt (ANG-30).
   *
   * Gesetzt heißt: Er steht für einen Wunsch aus einem Angebot, nicht für eine
   * Zusage — der Kalender kennzeichnet ihn als Option.
   */
  readonly optionsanfrage: string | null;
  readonly optionsstand: 'offen' | 'zugesagt' | 'abgelehnt' | 'vorschlag' | null;
  readonly angebotsnummer: string | null;
};

/**
 * Die Termine eines Zeitraums (TAG-29).
 *
 * Der Server begrenzt das Fenster auf ein Jahr — NFA-01 setzt eine Sekunde
 * für Listenansichten, gemessen an 20.000 Terminen.
 */
export async function kalender(
  von: string,
  bis: string,
  filter: { readonly artikel?: string; readonly ort?: string } = {},
): Promise<readonly Kalendertermin[]> {
  const p = new URLSearchParams({ von, bis });
  if (filter.artikel !== undefined && filter.artikel !== '') p.set('artikel', filter.artikel);
  if (filter.ort !== undefined && filter.ort !== '') p.set('ort', filter.ort);
  const antwort = await rufe<{ readonly termine: readonly Kalendertermin[] }>(
    `/termine/kalender?${p.toString()}`,
  );
  return antwort.termine;
}

/**
 * Alles zu einem Termin — für das Terminfenster (BUC-01, BUC-66, ÄA-04).
 *
 * Auftraggeber, 13.09.2026: „jeder Termin auswählbar … ein popup, das alle
 * Informationen zum Termin hält, bzw. Rückmeldung zu Optionen oder komplette
 * absagen ermöglicht."
 */
export type Terminakte = {
  readonly id: string;
  readonly artikel: string;
  readonly artikelnummer: string;
  readonly produkt: string;
  readonly terminart: 'offen' | 'exklusiv';
  readonly beginn: string;
  readonly ende: string;
  readonly ort: string | null;
  readonly status: string;
  readonly mindestteilnehmer: number;
  readonly hoechstteilnehmer: number;
  /** Vergebene Plätze in der anonymen Sicht (KAP-22). */
  readonly belegt: number;
  readonly betriebstagFreigegeben: boolean;
  readonly herkunftAngebot: string | null;
  readonly angebotsnummer: string | null;
  readonly herkunftAuftrag: string | null;
  readonly auftragsnummer: string | null;
  /** Der Kunde des Herkunftsbelegs — für den Weg zur Kundenakte (CRM-117). */
  readonly kunde: string | null;
  readonly kundenname: string | null;
  /** Gesetzt heißt: Der Termin entstand aus einem Wunsch (ANG-30). */
  readonly optionsanfrage: string | null;
  readonly buchungen: number;
  readonly gebuchtePlaetze: number;
  /** Teilnehmende mit Check-in oder erfasster Anwesenheit (TAG-59). */
  readonly anwesenheitErfasst: number;
  /**
   * BUC-129, BUC-131: die letzte Absage mit Grund und ihren Fällen in der
   * Absageliste; `null`, solange der Termin nicht abgesagt ist.
   */
  readonly absage: {
    readonly grund: string | null;
    readonly am: string;
    readonly faelle: number;
    readonly offen: number;
  } | null;
  /** BUC-11: ob das Produkt eine Warteliste führt. */
  readonly warteliste: boolean;
  /** DAT-08: die Vereinbarung am Termin. `null` ohne und bei offenen Terminen. */
  readonly vereinbarung: Exklusivvereinbarung | null;
  /** KAP-20: wie sich die freie Kapazität zusammensetzt, mit Wegkontingent. */
  readonly kapazitaet: Kapazitaetszusammensetzung;
  readonly ressourcen: readonly {
    readonly belegung: string;
    readonly kennung: string;
    readonly bezeichnung: string;
    readonly art: string;
    readonly status: string;
    /** RES-22: hart heißt verbindlich belegt, weich heißt vorgemerkt. */
    readonly hart: boolean;
  }[];
};

export type Buchungsweg = 'portal' | 'innendienst' | 'angebot';

export type Halteart =
  | 'buchung'
  | 'reservierung'
  | 'freigabe_zahlung'
  | 'option'
  | 'angebotsvormerkung';

/**
 * Die Aufstellung nach KAP-20. Gerechnet wird in der API (`@crm/kalkulation`);
 * die Maske zeigt an und rechnet nichts nach.
 */
export type Kapazitaetszusammensetzung = {
  readonly massgeblicheKapazitaet: number;
  readonly begrenzendeGroesse: 'hoechstteilnehmer' | 'ressourcen' | 'tagesplan';
  readonly plaetzeJeHalteart: Readonly<Record<Halteart, number>>;
  readonly gehaltenGesamt: number;
  readonly kontingentGesamt: number;
  readonly freiAnonym: number;
  readonly ueberbelegt: boolean;
  readonly wege: readonly Wegstand[];
};

/** BUC-71, AeA-03: das Kontingent eines Buchungswegs am Termin. */
export type Wegstand = {
  readonly weg: Buchungsweg;
  readonly regel: {
    readonly plaetze: number;
    readonly art: 'obergrenze' | 'reserviert';
    readonly laeuftAbAm: string | null;
  } | null;
  readonly regelWirksam: boolean;
  readonly gehalten: number;
  readonly frei: number;
  readonly begrenzenderWeg: 'obergrenze' | 'fremde_reservierung' | null;
};

/** Das Wegkontingent setzen — anlegen oder ändern (BUC-71). */
export async function wegregelSetzen(
  termin: string,
  weg: Buchungsweg,
  regel: { readonly plaetze: number; readonly art: 'obergrenze' | 'reserviert' },
): Promise<void> {
  await rufe(`/termine/${encodeURIComponent(termin)}/wegregeln/${weg}`, {
    verfahren: 'PUT',
    rumpf: regel,
  });
}

/** Das Wegkontingent entfernen — danach begrenzt nichts mehr den Weg. */
export async function wegregelEntfernen(termin: string, weg: Buchungsweg): Promise<void> {
  await rufe<void>(`/termine/${encodeURIComponent(termin)}/wegregeln/${weg}`, {
    verfahren: 'DELETE',
  });
}

/**
 * Die Exklusivvereinbarung (DAT-08, PRD-46).
 *
 * ENTWEDER eine feste Pauschale ODER eine Staffel — beides zugleich lässt die
 * Datenbank nicht zu. `preis` ist das Ergebnis; `null` heißt bei einer Staffel,
 * dass sie die vereinbarte Kapazität nicht abdeckt und der Termin damit keinen
 * Preis hat.
 */
export type Exklusivvereinbarung = {
  readonly kunde: string;
  readonly kundenname: string;
  readonly vereinbarteKapazitaet: number;
  readonly pauschale: string | null;
  readonly staffelnd: boolean;
  readonly istBrutto: boolean;
  readonly preis: string | null;
  readonly meldefrist: string | null;
  readonly ueberschreitungsregel: 'zusatzleistung' | 'abweisen';
  readonly ueberschreitungshinweis: string | null;
  readonly bemerkung: string | null;
  /** PRF-16: Steht sie auf einem Beleg beim Kunden, ist der Preis fest. */
  readonly preisFestgeschrieben: boolean;
  readonly staffel: readonly {
    readonly abKapazitaet: number;
    readonly pauschale: string;
  }[];
};

/**
 * Die Vereinbarung pflegen (DAT-08).
 *
 * GANZ UND NICHT IN TEILEN: Pauschale und Staffel schließen einander aus, und
 * die Stufen einer Staffel sind EIN Preisgefüge. Ein Teilschreiben ließe einen
 * Zwischenstand zu, den es nicht geben darf.
 */
export async function vereinbarungSetzen(
  termin: string,
  eingabe: {
    readonly kunde: string;
    readonly vereinbarteKapazitaet: number;
    readonly pauschale: string | null;
    readonly istBrutto: boolean;
    readonly meldefrist: string | null;
    readonly ueberschreitungsregel: 'zusatzleistung' | 'abweisen';
    readonly ueberschreitungshinweis: string | null;
    readonly bemerkung: string | null;
    readonly staffel: readonly { readonly abKapazitaet: number; readonly pauschale: string }[];
  },
): Promise<void> {
  await rufe(`/termine/${termin}/vereinbarung`, { verfahren: 'PUT', rumpf: eingabe });
}

export async function akte(id: string): Promise<Terminakte> {
  return rufe(`/termine/${encodeURIComponent(id)}/akte`);
}

/** Was zu einer Person am Termin eingetragen wird (BUC-69). */
export type Personangabe = {
  readonly name: string;
  readonly email?: string;
  readonly geburtsdatum?: string;
};

export type Erfassungsergebnis = {
  readonly buchung: string;
  readonly buchungsnummer: string;
  readonly erfasst: number;
  readonly gemeldet: number;
  readonly vereinbarteKapazitaet: number;
  /** BUC-82: ausgewiesen, nicht abgewiesen. */
  readonly ueberschritten: boolean;
};

/** BUC-68, BUC-69: Teilnehmende am Exklusivtermin erfassen (CRM-117). */
export async function teilnehmerErfassen(
  termin: string,
  teilnehmer: readonly Personangabe[],
): Promise<Erfassungsergebnis> {
  return rufe(`/termine/${encodeURIComponent(termin)}/teilnehmer`, {
    verfahren: 'POST',
    rumpf: { teilnehmer },
  });
}

/** BUC-70: einen Platz ohne Namen benennen (CRM-117). */
export async function platzBenennen(
  termin: string,
  teilnehmer: string,
  angabe: Personangabe,
): Promise<{ readonly teilnehmer: string; readonly name: string }> {
  return rufe(
    `/termine/${encodeURIComponent(termin)}/teilnehmer/${encodeURIComponent(teilnehmer)}`,
    { verfahren: 'PUT', rumpf: angabe },
  );
}

/** Alle Termine eines Produkts — die Sicht der Terminverwaltung. */
export async function alleZumArtikel(artikel: string): Promise<readonly Terminuebersicht[]> {
  const antwort = await rufe<{ readonly termine: readonly Terminuebersicht[] }>(
    `/termine/alle?artikel=${encodeURIComponent(artikel)}`,
  );
  return antwort.termine;
}

/**
 * Ein Exklusivtermin mit dem, was seine Vereinbarung sagt (DAT-08).
 *
 * `pauschale` ist der aufgelöste Betrag — bei einer Pauschalstaffel die
 * Stufe, welche die vereinbarte Kapazität erreicht (PRD-45). Er steht hier,
 * damit die Belegmaske VOR dem Anlegen zeigen kann, welcher Preis gelten
 * wird: Nach PRD-46 ist er der Rechnungsbetrag und nicht der Listenpreis.
 */
export type Exklusivtermin = {
  readonly id: string;
  readonly artikel: string;
  readonly artikelnummer: string;
  readonly artikelbezeichnung: string;
  readonly beginn: string;
  readonly ende: string;
  readonly ort: string | null;
  readonly status: string;
  readonly hoechstteilnehmer: number;
  readonly vereinbarteKapazitaet: number;
  readonly pauschale: string | null;
  readonly istBrutto: boolean;
};

/**
 * Die Exklusivtermine eines Kunden (BUC-67, ANG-25, AUF-05).
 *
 * Eigener Weg, weil `buchbare` nach BUC-67 nur offene Termine führt:
 * „Exklusivtermine erscheinen nicht in der öffentlichen
 * Verfügbarkeitsauskunft." Für die Belegerfassung braucht der Innendienst
 * sie trotzdem — der Termin IST dort der Gegenstand der Position.
 */
export async function exklusive(
  kunde: string,
  artikel?: string,
): Promise<readonly Exklusivtermin[]> {
  const p = new URLSearchParams({ kunde });
  if (artikel !== undefined && artikel !== '') p.set('artikel', artikel);
  const antwort = await rufe<{ readonly termine: readonly Exklusivtermin[] }>(
    `/termine/exklusiv?${p.toString()}`,
  );
  return antwort.termine;
}

/** Wer angeschrieben wurde — und wer nicht, mit Grund (BUC-138). */
export type Benachrichtigt = {
  readonly buchung: string;
  readonly buchungsnummer: string;
  readonly nachricht: string | null;
  readonly grund: string | null;
  /** BUC-07: eine Reservierung ohne Buchung — kein Empfaenger, laeuft ab. */
  readonly reservierung?: true;
};

export type Serienmuster =
  | { readonly art: 'abstand'; readonly tage: number }
  | { readonly art: 'monatlich'; readonly wochentag: number; readonly woche: 1 | 2 | 3 | 4 | 'letzte' };

/**
 * Die buchbaren Termine zu einem Produkt.
 *
 * `anfragender` und `fuerKundenart` sind nicht schmueckendes Beiwerk:
 *
 *   KAP-04  Ohne den Anfragenden rechnet die freie Kapazität die ANONYME
 *           Sicht (KAP-22). Ein Kunde mit eigenem Kontingent bekäme "voll"
 *           gemeldet, obwohl Plätze für ihn zurückliegen.
 *   BUC-105 Ohne die Kundenart stünden hier Termine zu einem Produkt, das
 *           der Abschluss danach abweist.
 */
export async function buchbare(
  artikel: string,
  ausser?: string,
  anfragender?: string,
  fuerKundenart?: string,
): Promise<readonly Terminauswahl[]> {
  const p = new URLSearchParams({ artikel });
  if (ausser !== undefined) p.set('ausser', ausser);
  if (anfragender !== undefined && anfragender !== '') p.set('anfragender', anfragender);
  if (fuerKundenart !== undefined && fuerKundenart !== '') {
    p.set('fuerKundenart', fuerKundenart);
  }
  const antwort = await rufe<{ readonly termine: readonly Terminauswahl[] }>(
    `/termine?${p.toString()}`,
  );
  return antwort.termine;
}

/**
 * Einen EINZELNEN Termin anlegen (BUC-01) — der Regelfall.
 *
 * `preisAbweichend` ist eine Zeichenkette: `129.90` ist als JSON-Zahl nicht
 * exakt darstellbar, und über eine Gleitkommazahl liefe der Preis, nach dem
 * abgerechnet wird (RND-01).
 *
 * Die vier Fristen sind MINUTEN relativ zum Termin (BUC-04). Sie ziehen bei
 * einer Verlegung von selbst mit — ein absoluter Zeitpunkt müsste nachgezogen
 * werden und wäre beim ersten Vergessen falsch.
 */
export async function anlegen(eingabe: {
  /**
   * BUC-68: „Ein Exklusivtermin entsteht aus einem Angebot oder Auftrag."
   * Bei `terminart: 'exklusiv'` ist eines der beiden Pflicht — ohne es weist
   * der CHECK `termin_herkunft` den Termin ab, und der Anwender bekam
   * „unerwartet" zu sehen.
   */
  readonly herkunftAuftrag?: string;
  readonly herkunftAngebot?: string;
  artikel: string;
  beginn: string;
  ende: string;
  hoechstteilnehmer: number;
  mindestteilnehmer?: number;
  terminart?: 'offen' | 'exklusiv';
  ort?: string;
  /*
   * HIER STAND `gruppen` (TAG-04, TAG-05 — entfallen mit ÄA-09). Mehrere
   * Gruppen sind mehrere Termine; `fach.gruppe` ist seit Migration 0140 weg.
   */
  preisAbweichend?: string;
  ressourcenObergrenze?: number;
  tagesplanPlatzzahl?: number;
  freigabeErforderlich?: boolean;
  /*
   * BUC-04 in der Einheit aus Tabelle 24: KALENDERTAGE, nicht Minuten. Bis
   * zur Migration 0114 standen hier Minuten — und die Frist waere damit an
   * einem Termin um 8:00 sechzehn Stunden zu frueh abgelaufen, weil FRS-04
   * am Tag „Beginn minus n" die Uhrzeit 23:59 vorsieht.
   */
  fruehesteBuchungTage?: number;
  spaetesteBuchungTage?: number;
  stornofristTage?: number;
  umbuchungsfristTage?: number;
  /** FRS-04: die Uhrzeit des Buchungsschlusses als HH:MM, Vorgabe 23:59. */
  buchungsschlussUhr?: string;
  /** Tabelle 27, Zeile 2: gleich zum Verkauf freigeben (0252). */
  freigeben?: boolean;
}): Promise<{ readonly id: string }> {
  return rufe('/termine', { verfahren: 'POST', rumpf: eingabe });
}

export async function serie(kennung: string): Promise<readonly Terminzeile[]> {
  const antwort = await rufe<{ readonly termine: readonly Terminzeile[] }>(
    `/termine/serie/${encodeURIComponent(kennung)}`,
  );
  return antwort.termine;
}

export async function serieAnlegen(eingabe: {
  artikel: string;
  ersterBeginn: string;
  dauerMinuten: number;
  anzahl: number;
  hoechstteilnehmer: number;
  mindestteilnehmer?: number;
  ort?: string;
  muster: Serienmuster;
  /** Tabelle 27, Zeile 2: jeden Termin der Serie gleich freigeben (0252). */
  freigeben?: boolean;
}): Promise<{ readonly serie: string; readonly termine: readonly string[] }> {
  return rufe('/termine/serie', { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Die Verkaufsfreigabe (Tabelle 27, Zeile 2): „geplant" → „buchbar". Erst
 * danach erscheint der Termin im Portal und in der Buchungsauswahl.
 */
export async function freigeben(termin: string): Promise<{ readonly freigegeben: boolean }> {
  return rufe(`/termine/${encodeURIComponent(termin)}/freigeben`, { verfahren: 'POST' });
}

/**
 * Die Durchführung bestätigen (Tabelle 27, Zeile 9; BUC-05).
 *
 * Endzustand: Der Termin verlässt „durchgeführt" nicht mehr (Zeile 11). Die
 * Maske fragt deshalb vorher nach.
 */
export async function durchfuehrungBestaetigen(
  termin: string,
): Promise<{ readonly status: 'durchgefuehrt' }> {
  return rufe(`/termine/${encodeURIComponent(termin)}/durchfuehrung`, { verfahren: 'POST' });
}

/** Ein beendeter Termin, dessen Durchführung noch niemand bestätigt hat. */
export type OffeneDurchfuehrung = {
  readonly id: string;
  readonly produkt: string;
  readonly terminart: 'offen' | 'exklusiv';
  readonly beginn: string;
  readonly ende: string;
  readonly status: string;
  readonly ort: string | null;
  readonly teilnehmende: number;
  readonly anwesenheitErfasst: number;
};

/** Die Arbeitsliste „Durchführung noch nicht bestätigt" (Tabelle 27, Zeile 9). */
export async function offeneDurchfuehrungen(): Promise<readonly OffeneDurchfuehrung[]> {
  const { termine } = await rufe<{ readonly termine: readonly OffeneDurchfuehrung[] }>(
    '/termine/durchfuehrung/offen',
  );
  return termine;
}

/**
 * Verlegen (BUC-138, ÄA-04).
 *
 * Die Antwort nennt die ANGESCHRIEBENEN mit — und die, bei denen es nicht
 * ging, mit ihrem Grund. Das ist die Arbeitsliste zur Einzelbehandlung; sie
 * zu verschweigen hieße, dass ein Kunde ohne E-Mail-Adresse still übergangen
 * wird und am alten Datum vor der Tür steht.
 */
export async function verlegen(
  termin: string,
  /** EB-DP-07: `dauerAendern` erlaubt eine andere Dauer; ohne es bleibt sie gleich. */
  eingabe: { beginnNeu: string; endeNeu: string; dauerAendern?: true; grund?: string },
): Promise<{ readonly art: 'verlegt'; readonly angeschrieben: readonly Benachrichtigt[] }> {
  return rufe(`/termine/${termin}/verlegen`, { verfahren: 'POST', rumpf: eingabe });
}

/**
 * Absagen (ÄA-04). Der Grund ist Pflicht — er steht im Anschreiben.
 *
 * ÄW-DP-02: Ein abgesagter oder durchgeführter Termin ergibt 409
 * (`termin_nicht_aenderbar`) und wirft hier einen ApiFehler mit dem Grund —
 * Verlegen wie Absagen. Eine Antwort heißt also: Es ist geschehen.
 */
export async function absagen(
  termin: string,
  grund: string,
): Promise<{ readonly art: 'abgesagt'; readonly angeschrieben: readonly Benachrichtigt[] }> {
  return rufe(`/termine/${termin}/absagen`, { verfahren: 'POST', rumpf: { grund } });
}

/**
 * Die Standorte zur Auswahl (RES-01).
 *
 * `termin.ort` trägt einen Fremdschlüssel auf `standort.kennung`. Ein
 * Freitextfeld darauf weist jeden Tippfehler ab und lässt bei leerer Eingabe
 * einen Termin ohne Ort entstehen — der gehört dann zu keinem Betriebstag und
 * erscheint in keiner Tagesplanung.
 */
export async function standorte(): Promise<{
  readonly standorte: readonly { readonly kennung: string; readonly bezeichnung: string }[];
}> {
  return rufe('/termine/standorte');
}
